/**
 * Sellox POS - IndexedDB Storage for Offline Data
 * Handles large datasets (products, pending orders) that exceed localStorage limits.
 * Falls back to localStorage if IndexedDB is unavailable.
 */

const DB_NAME = 'sellox_pos_db';
const DB_VERSION = 1;
const STORES = {
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  CUSTOMERS: 'customers',
  COMBOS: 'combos',
  PENDING_ORDERS: 'pending_orders',
  META: 'meta',
};

let dbInstance = null;

function openDB() {
  return new Promise((resolve, reject) => {
    if (dbInstance) { resolve(dbInstance); return; }
    if (!window.indexedDB) { reject(new Error('IndexedDB not supported')); return; }

    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      Object.values(STORES).forEach(name => {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: 'key' });
        }
      });
    };
    request.onsuccess = (event) => { dbInstance = event.target.result; resolve(dbInstance); };
    request.onerror = (event) => { reject(event.target.error); };
  });
}

// Save data to IndexedDB
export async function idbSave(storeName, key, data) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.put({ key, data, updated: new Date().toISOString() });
      tx.oncomplete = () => resolve(true);
      tx.onerror = (e) => reject(e.target.error);
    });
  } catch (e) {
    // Fallback to localStorage
    try {
      localStorage.setItem(`pos_idb_${storeName}_${key}`, JSON.stringify({ data, updated: new Date().toISOString() }));
    } catch { /* storage full */ }
    return false;
  }
}

// Load data from IndexedDB
export async function idbLoad(storeName, key, fallback = null) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.get(key);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : fallback);
      };
      request.onerror = () => resolve(fallback);
    });
  } catch {
    // Fallback to localStorage
    try {
      const raw = localStorage.getItem(`pos_idb_${storeName}_${key}`);
      return raw ? JSON.parse(raw).data : fallback;
    } catch { return fallback; }
  }
}

// Get update timestamp for a key
export async function idbGetMeta(storeName, key) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.get(key);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result?.updated || null);
      };
      request.onerror = () => resolve(null);
    });
  } catch { return null; }
}

// Delete a key from IndexedDB
export async function idbDelete(storeName, key) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.delete(key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch { return false; }
}

// Clear entire store
export async function idbClear(storeName) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      store.clear();
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch { return false; }
}

// === Pending Orders specific helpers ===

// Triple-redundant pending-order persist.
//
// 2026-05-10 incident at ALFONZO SHOP: a credit order was printed for an
// employee but vanished — IndexedDB silently failed (quota / private window /
// disk pressure on the kiosk), the outer try/catch swallowed it, the cashier
// saw a green toast, the customer left with the goods, and the order was
// never seen again.
//
// Hardening:
//  1. Try IndexedDB.
//  2. If that throws, mirror to localStorage 'pos_pending_orders' array.
//  3. Always also write a flat "lifeboat" copy keyed by offline_id under
//     `pos_lifeboat_<id>` so even if the queue array is wiped, we can still
//     recover the order from any leftover key. The lifeboat is only deleted
//     once the order is confirmed synced (removePendingOrder).
//  4. Throw on total failure so callers can show a BIG warning instead of a
//     misleading "✓ saved" toast.
// On Sellox Desktop (Windows app) pending orders are stored on DISK via the
// Electron bridge (survive reboots / cache clears). Browser uses the layers below.
const _desktop = () => (typeof window !== 'undefined' && window.selloxDesktop) || null;

export async function addPendingOrder(order) {
  const id = order.offline_id || `off_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const _d = _desktop();
  if (_d) { await _d.addOrder(id, { ...order, offline_id: id }); return id; }
  let idbOk = false;
  try {
    await idbSave(STORES.PENDING_ORDERS, id, { ...order, offline_id: id });
    idbOk = true;
  } catch (e) {
    console.warn("[offlineDB] IndexedDB save failed:", e);
  }
  // Mirror to localStorage queue (even on IDB success — small belt-and-braces)
  let lsOk = false;
  try {
    const raw = localStorage.getItem("pos_pending_orders");
    const arr = raw ? JSON.parse(raw) : [];
    if (!arr.find((o) => (o.offline_id || "") === id)) {
      arr.push({ ...order, offline_id: id });
      localStorage.setItem("pos_pending_orders", JSON.stringify(arr));
    }
    lsOk = true;
  } catch (e) {
    console.warn("[offlineDB] localStorage queue save failed:", e);
  }
  // Lifeboat — flat single-key copy. Survives even if the queue array gets
  // corrupted or wiped by another tab.
  let lifeboatOk = false;
  try {
    localStorage.setItem(`pos_lifeboat_${id}`, JSON.stringify({ ...order, offline_id: id, _lifeboat_at: new Date().toISOString() }));
    lifeboatOk = true;
  } catch (e) {
    console.warn("[offlineDB] lifeboat save failed:", e);
  }
  if (!idbOk && !lsOk && !lifeboatOk) {
    throw new Error("OFFLINE_PERSIST_FAILED — order could not be saved anywhere");
  }
  return id;
}

// Get all pending orders
export async function getAllPendingOrders() {
  const _d = _desktop();
  if (_d) { try { return await _d.getAllOrders(); } catch { return []; } }
  let idbOrders = [];
  try {
    const db = await openDB();
    idbOrders = await new Promise((resolve) => {
      const tx = db.transaction(STORES.PENDING_ORDERS, 'readonly');
      const store = tx.objectStore(STORES.PENDING_ORDERS);
      const request = store.getAll();
      request.onsuccess = () => resolve((request.result || []).map(r => r.data));
      request.onerror = () => resolve([]);
    });
  } catch { /* fall through to LS merge below */ }
  // Always merge the localStorage queue + lifeboat copies — they are the
  // backup-of-last-resort if IDB ever loses data mid-shift.
  const seen = new Set(idbOrders.map((o) => o.offline_id || ""));
  try {
    const raw = localStorage.getItem("pos_pending_orders");
    const arr = raw ? JSON.parse(raw) : [];
    for (const o of arr) {
      const oid = o.offline_id || "";
      if (oid && !seen.has(oid)) { idbOrders.push(o); seen.add(oid); }
    }
  } catch { /* ignore */ }
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith("pos_lifeboat_")) continue;
      const oid = k.slice("pos_lifeboat_".length);
      if (seen.has(oid)) continue;
      try {
        const o = JSON.parse(localStorage.getItem(k));
        if (o) { idbOrders.push(o); seen.add(oid); }
      } catch { /* corrupt entry — skip */ }
    }
  } catch { /* ignore */ }
  return idbOrders;
}

// Remove a synced pending order — wipes ALL three storage layers.
export async function removePendingOrder(offlineId) {
  const _d = _desktop();
  if (_d) { try { await _d.removeOrder(offlineId); } catch { /* ignore */ } return; }
  try { await idbDelete(STORES.PENDING_ORDERS, offlineId); } catch { /* ignore */ }
  try {
    const raw = localStorage.getItem("pos_pending_orders");
    if (raw) {
      const arr = JSON.parse(raw);
      const next = arr.filter((o) => (o.offline_id || "") !== offlineId);
      localStorage.setItem("pos_pending_orders", JSON.stringify(next));
    }
  } catch { /* ignore */ }
  try { localStorage.removeItem(`pos_lifeboat_${offlineId}`); } catch { /* ignore */ }
}

// Get pending order count
export async function getPendingOrderCount() {
  const orders = await getAllPendingOrders();
  return orders.length;
}

// === USB Offline Backup: export ALL pending orders to a JSON file (for transfer via USB) ===
export async function exportAllPendingAsJSON(meta = {}) {
  const orders = await getAllPendingOrders();
  return {
    format: 'sellox-offline-v1',
    exported_at: new Date().toISOString(),
    ...meta,
    order_count: orders.length,
    orders,
  };
}

// === USB Offline Restore: import JSON file's pending orders into the IndexedDB queue ===
// Returns {added, skipped, errors} — skips duplicates by offline_id.
export async function importPendingOrdersFromJSON(data) {
  if (!data || typeof data !== 'object') throw new Error('Invalid JSON');
  if (data.format && data.format !== 'sellox-offline-v1') {
    throw new Error(`Unsupported format: ${data.format}`);
  }
  const arr = Array.isArray(data.orders) ? data.orders : (Array.isArray(data) ? data : []);
  if (!arr.length) return { added: 0, skipped: 0, errors: 0, total: 0 };
  const existing = await getAllPendingOrders();
  const existingIds = new Set(existing.map(o => o.offline_id || `off_${o.created_at}`));
  let added = 0, skipped = 0, errors = 0;
  for (const order of arr) {
    try {
      const id = order.offline_id || `off_${order.created_at || Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      if (existingIds.has(id)) { skipped += 1; continue; }
      order.offline_id = id;
      order._imported_at = new Date().toISOString();
      await addPendingOrder(order);
      added += 1;
      existingIds.add(id);
    } catch {
      errors += 1;
    }
  }
  return { added, skipped, errors, total: arr.length };
}

// Get human-readable time ago
export function timeAgo(isoString) {
  if (!isoString) return null;
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '刚刚';
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  return `${days}天前`;
}

// Clear all data stores (products, categories, customers, combos) but keep pending_orders
export async function clearAllDataStores() {
  try {
    const db = await openDB();
    const storesToClear = [STORES.PRODUCTS, STORES.CATEGORIES, STORES.CUSTOMERS, STORES.COMBOS, STORES.META];
    for (const storeName of storesToClear) {
      try {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).clear();
        await new Promise((resolve) => { tx.oncomplete = resolve; tx.onerror = resolve; });
      } catch {}
    }
    return true;
  } catch { return false; }
}

// Nuke the entire IndexedDB database (for major version upgrades)
export async function deleteDatabase() {
  try {
    dbInstance = null;
    return new Promise((resolve) => {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);
      req.onblocked = () => resolve(false);
    });
  } catch { return false; }
}

export { STORES };
